import React from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { Flowbite } from 'flowbite-react'

import { useAuth } from './hooks/useAuth'
import { useDarkMode } from './hooks/useDarkMode'
import { useFamilies } from './hooks/useFamilies'

import Navbar from './components/layout/Navbar'
import ProtectedRoute from './components/layout/ProtectedRoute'
import AuthPage from './components/auth/AuthPage'
import JoinOrCreateFamily from './components/family/JoinOrCreateFamily'
import TreePage from './components/tree/TreePage'
import UnifiedAdmin from './components/admin/UnifiedAdmin'
import SettingsPage from './components/settings/SettingsPage'
import ErrorBoundary from './components/common/ErrorBoundary'

import 'flowbite'

function App() {
  const { user, loading: authLoading } = useAuth()
  const { dark, toggle } = useDarkMode()
  const { userFamilies, currentFamilyId, setCurrentFamilyId, loading: familiesLoading, refreshFamilies } = useFamilies(user)

  // Only show onboarding when user truly has zero families
  const showFamilyOnboarding = user && !authLoading && !familiesLoading && userFamilies.length === 0

  const handleFamilyCreated = async () => {
    await refreshFamilies()
  }

  return (
    <Flowbite theme={{ mode: dark ? 'dark' : 'light' }}>
      <Router>
        <Navbar
          user={user}
          families={userFamilies}
          currentFamilyId={currentFamilyId}
          onFamilyChange={setCurrentFamilyId}
          dark={dark}
          onToggleDark={toggle}
        />
        <main className="min-h-[calc(100vh-53px)] bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-950 dark:to-purple-950 text-gray-900 dark:text-gray-100 relative overflow-hidden">
          {/* Animated gradient orbs for depth */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-1/2 -left-1/4 w-96 h-96 bg-blue-400/30 dark:bg-blue-600/20 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute top-1/3 -right-1/4 w-96 h-96 bg-purple-400/30 dark:bg-purple-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
            <div className="absolute -bottom-1/2 left-1/3 w-96 h-96 bg-indigo-400/30 dark:bg-indigo-600/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
          </div>

          <div className="relative z-10">
            <ErrorBoundary>
              {showFamilyOnboarding ? (
                <JoinOrCreateFamily user={user} onFamilyCreated={handleFamilyCreated} />
              ) : (
                <Routes>
                  <Route path="/auth" element={<AuthPage />} />
                  <Route
                    path="/family/new"
                    element={
                      <ProtectedRoute user={user} familyId={currentFamilyId} loading={authLoading || familiesLoading}>
                        <JoinOrCreateFamily user={user} onFamilyCreated={handleFamilyCreated} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute user={user} familyId={currentFamilyId} loading={authLoading || familiesLoading}>
                        <TreePage user={user} familyId={currentFamilyId} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute user={user} familyId={currentFamilyId} loading={authLoading || familiesLoading}>
                        <UnifiedAdmin user={user} familyId={currentFamilyId} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute user={user} familyId={currentFamilyId} loading={authLoading || familiesLoading}>
                        <SettingsPage user={user} familyId={currentFamilyId} />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              )}
            </ErrorBoundary>
          </div>
        </main>
      </Router>
    </Flowbite>
  )
}

export default App
